import { AgGridReact } from 'ag-grid-react';
import { useCallback, useRef } from 'react';
import { PnlGridRow } from '../starGridTypes';

interface UseAgGridExpandedStateResult {
  readonly gridRef: React.RefObject<AgGridReact<PnlGridRow>>;
  /** Snapshot which tree rows are currently expanded so we can re-apply after a re-query. */
  readonly captureExpandedState: () => void;
  /** Called after row-data refresh — re-expands the previously captured node ids, then clears the buffer. */
  readonly restoreExpandedState: () => void;
}

/**
 * Preserves AG Grid tree-row expansion across drill-down re-queries. Ported verbatim from
 * xPT's useAgGridExpandedState — the grid component owns its own ref/state plumbing.
 */
export const useAgGridExpandedState = (): UseAgGridExpandedStateResult => {
  const gridRef = useRef<AgGridReact<PnlGridRow>>(null);
  const expandedRowIds = useRef<Set<string>>(new Set());

  const captureExpandedState = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const expanded = new Set<string>();
    api.forEachNode((node) => {
      if (node.expanded && node.id) expanded.add(node.id);
    });
    expandedRowIds.current = expanded;
  }, []);

  const restoreExpandedState = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || expandedRowIds.current.size === 0) return;
    api.forEachNode((node) => {
      if (node.id && expandedRowIds.current.has(node.id)) node.setExpanded(true);
    });
    expandedRowIds.current = new Set();
  }, []);

  return { gridRef, captureExpandedState, restoreExpandedState };
};
